import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  Output
} from '@angular/core';
import { Subscribable } from '../../../../models/base/subscribable';
import { MembersFileRecord } from './interfaces/members-file-record';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';
import { fileFormattingErrorValidator } from '../../validators/file-formatting-error-validator';
import { ModalUtils } from '../../../../utils/modal-utils';
import { BatchAddMembersFilePreviewModalComponent } from '../batch-add-members-file-preview-modal/batch-add-members-file-preview-modal.component';
import { ConfirmationOptions } from '../../../../models/shared/stylesheet/confirmation-options';
import { ModalConfirmation } from '../../../../modals/modal-confirmation';
import { CSS_NEW_MEMBER_TEMPLATE_LINK } from '../../../../consts/consts';
import { ButtonTypeEnum } from '../../../../ui/buttons/button/button-type.enum';

@Component({
  selector: 'app-batch-add-members-file-upload',
  templateUrl: './batch-add-members-file-upload.component.html',
  styleUrls: ['./batch-add-members-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchAddMembersFileUploadComponent extends Subscribable implements AfterViewInit {
  @Input() cardTitle: string = '';
  @Input() memberType: 'member' | 'employee' = 'member';
  @Output() membersAdded = new EventEmitter<MembersFileRecord[]>();

  private ngbModal = inject(NgbModal);
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);

  private _hasFormattingError = new BehaviorSubject<boolean>(false);
  public hasFormattingError$ = this._hasFormattingError as Observable<boolean>;

  private membersToAdd: MembersFileRecord[] = [];

  public formGroup = new FormGroup({
    file: new FormControl<File[]>([], {
      validators: [
        Validators.required,
        maxFileSizeValidator(3),
        fileFormattingErrorValidator(this.hasFormattingError$)
      ],
      nonNullable: true
    })
  });

  public openPreviewModal(f: File): void {
    const modalRef = this.ngbModal.open(
      BatchAddMembersFilePreviewModalComponent,
      ModalUtils.defaultExtraLargeModalOptions(this.injector)
    );
    modalRef.componentInstance.setFileToPreview(f);
    modalRef.result.then(res => {
      if (res?.records) {
        this.membersToAdd = res.records;
        if (res?.continue) this.submitForm();
      } else if (res?.closedWithErrors) {
        this._hasFormattingError.next(true);
      } else if (res?.discardUpload) {
        this.removeFile();
      } else {
        this._hasFormattingError.next(false);
      }
      this.cdr.detectChanges();
    });
  }

  public onRemoveFile(): void {
    this.hasFormattingError$.once(hasError => {
      if (hasError) {
        this.removeFile();
      } else {
        const opts = new ConfirmationOptions();
        opts.title = $localize`Discard this Upload?`;
        opts.bodyText = $localize`Are you sure you want to discard this file?`;
        opts.continueText = $localize`Discard`;
        opts.cancelText = $localize`Cancel`;
        ModalConfirmation.open(this.ngbModal, this.injector, opts, confirm => {
          if (confirm) {
            this.removeFile();
          }
        });
      }
    });
  }

  private removeFile(): void {
    this._hasFormattingError.next(false);
    this.formGroup.controls.file?.reset();
    this.cdr.detectChanges();
  }

  public submitForm(): void {
    this.membersAdded.emit(this.membersToAdd);
  }

  public downloadTemplate(): void {
    window.open(CSS_NEW_MEMBER_TEMPLATE_LINK, '_blank');
  }

  public skipFileUpload(): void {
    this.membersAdded.emit([]);
  }

  public ngAfterViewInit(): void {
    this.formGroup.valueChanges.subscribeWhileAlive({
      owner: this,
      next: formVal => {
        const file = formVal.file;
        if (file && file.length > 0) {
          this.openPreviewModal(file[0]);
        }
      }
    });

    this.hasFormattingError$.subscribeWhileAlive({
      owner: this,
      next: hasError => {
        const fileCtrl = this.formGroup.controls.file;
        if (!fileCtrl) return;

        if (hasError) {
          fileCtrl.setErrors({ ...(fileCtrl.errors ?? {}), formattingError: true });
        } else {
          if (fileCtrl.errors) {
            delete fileCtrl.errors.formattingError;
            fileCtrl.setErrors(Object.keys(fileCtrl.errors).length ? fileCtrl.errors : null);
          }
        }
        fileCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.cdr.detectChanges();
      }
    });
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
